import { useRef, useState, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { motion, AnimatePresence } from 'framer-motion'
import { AlbumCard } from './AlbumCard'
import { TitleDetailModal } from './TitleDetailModal'
import { TitleSearchDialog } from './TitleSearchDialog'

interface Title {
  id: string
  name: string
  nameJa?: string
  posterUrl?: string
  backdropUrl?: string
  viewCount: number
  mediaType: 'movie' | 'tv'
  genres: string[]
  firstWatched: string
  lastWatched: string
  tmdbId?: number
}

interface AlbumGridProps {
  titles: Title[]
}

export function AlbumGrid({ titles }: AlbumGridProps) {
  const parentRef = useRef<HTMLDivElement>(null)
  const [selectedTitle, setSelectedTitle] = useState<Title | null>(null)
  const [searchingTitle, setSearchingTitle] = useState<Title | null>(null)

  // Determine column count and row height based on viewport
  const getColumnCount = () => {
    if (typeof window === 'undefined') return 6
    const width = window.innerWidth
    if (width < 640) return 2  // mobile
    if (width < 768) return 3  // sm
    if (width < 1024) return 4 // md
    return 6                   // lg+
  }

  const getRowHeight = () => {
    if (typeof window === 'undefined') return 300
    const width = window.innerWidth
    const columnCount = getColumnCount()

    // Adjust padding and gap based on screen size
    const padding = width < 640 ? 24 : 32 // 3*2 for mobile, 4*2 for larger
    const gap = width < 640 ? 12 : 16

    const containerWidth = Math.min(width - padding, 1280) // max container width
    const cardWidth = (containerWidth - (columnCount - 1) * gap) / columnCount
    return cardWidth * 1.5 + gap // aspect ratio 2:3 plus gap
  }

  const [columnCount, setColumnCount] = useState(getColumnCount())
  const [rowHeight, setRowHeight] = useState(getRowHeight())

  // Update on resize
  useEffect(() => {
    const handleResize = () => {
      setColumnCount(getColumnCount())
      setRowHeight(getRowHeight())
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(titles.length / columnCount),
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 5
  })

  return (
    <div
      ref={parentRef}
      className="h-[calc(100vh-300px)] overflow-auto"
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative'
        }}
      >
        <AnimatePresence mode="popLayout">
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const startIndex = virtualRow.index * columnCount
            const endIndex = Math.min(startIndex + columnCount, titles.length)

            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`
                }}
                className="grid grid-cols-2 gap-3 px-3 sm:gap-4 sm:px-4 md:grid-cols-4 lg:grid-cols-6"
              >
                {titles.slice(startIndex, endIndex).map((title) => (
                  <motion.div
                    key={title.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                    className="relative overflow-visible"
                  >
                    <AlbumCard title={title} onClick={() => setSelectedTitle(title)} />
                  </motion.div>
                ))}
              </div>
            )
          })}
        </AnimatePresence>
      </div>

      {selectedTitle && (
        <TitleDetailModal
          title={selectedTitle}
          onClose={() => setSelectedTitle(null)}
          onOpenSearch={() => {
            setSearchingTitle(selectedTitle)
            setSelectedTitle(null)
          }}
        />
      )}

      {searchingTitle && (
        <TitleSearchDialog
          titleId={searchingTitle.id}
          currentName={searchingTitle.name}
          onClose={() => setSearchingTitle(null)}
        />
      )}
    </div>
  )
}
